import type { Eta } from '../eta/eta.interface';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { Order, TimeSlot } from '../order/order.interface';
import type { RoutingVehicleProfile } from '../routing/routing.interface';
import type { Task, TaskJob, TaskJobType } from '../task/task.interface';
import type { UserComment } from '../user-comment/user-comment.interface';

export const STOPS_QUEUE_STATUS = {
  ENDED: 'ended',
  ONGOING: 'ongoing',
  PLANNED: 'planned',
} as const;

export type StopsQueueStatus = (typeof STOPS_QUEUE_STATUS)[keyof typeof STOPS_QUEUE_STATUS];

/** Who last computed a queue's `routeSummary` — the ETA engine (every recompute) or a confirmed optimization (its own totals). */
export const ROUTE_SUMMARY_SOURCE = { ETA: 'eta', OPTIMIZATION: 'optimization' } as const;
export type RouteSummarySource = (typeof ROUTE_SUMMARY_SOURCE)[keyof typeof ROUTE_SUMMARY_SOURCE];

/** Distance/duration totals for a driver's whole remaining queue, as last computed by `source`. */
export interface StopsQueueRouteSummary {
  computedAt: Date;
  distanceMeters: number;
  durationSeconds: number;
  source: RouteSummarySource;
}

/**
 * A single job grouped into a stop — a flattened summary of the task job it belongs to (the
 * TaskJob's own `id`, `type`, and `sortIndex`), plus the `taskId`/`orderId` it references.
 */
export interface StopJob {
  id: string;
  orderId: string;
  sortIndex: number;
  taskId: string;
  type: TaskJobType;
}

/**
 * A stop is a visit that groups one or more jobs (e.g. multiple pickups at the same
 * location). `id` is a stable, generated identifier for the stop subdocument. `fullAddress`
 * and `timeSlot` are denormalized from the stop's single source job at insert time (see
 * `buildStop()` in `apps/pharos-back-deliveries/src/stops-queue/queue-ordering.ts`) so
 * `insertTaskStops` can order the queue without re-fetching the task/job documents.
 */
export interface Stop {
  /**
   * Client-declared time of `declareArrival` (stops-queue `timestamp` body field) — never
   * re-stamped by `waiting_for_pickup` (not a stop-level event). Feeds the ETA anchor resolver's
   * `arrived_at_stop` candidate.
   */
  arrivedAt?: Date;
  /**
   * Client-declared time of the most recent `completeStopJobs` targeting this stop — re-stamped
   * on each terminal job action at the stop. Feeds the ETA anchor resolver's `completed_job`
   * candidate.
   */
  completedJobAt?: Date;
  /** Stamped by the ETA engine (`pharos-back-deliveries`) whenever the queue or its execution state changes. */
  eta?: Eta;
  /**
   * Client-declared time of the most recent `failStopJobs` targeting this stop — re-stamped on
   * each terminal job action at the stop. Feeds the ETA anchor resolver's `completed_job`
   * candidate.
   */
  failedJobAt?: Date;
  fullAddress: string;
  id: string;
  /**
   * The first ETA the engine ever stamped on this stop — write-once, never overwritten once set.
   * Kept for ETA-accuracy measurement against the stop's actual arrival; `eta` keeps updating with
   * every recompute.
   */
  initialEta?: Eta;
  jobs: StopJob[];
  /**
   * Neutral encoded geometry (Google encoded polyline, precision 5) of the leg leading to this
   * stop — from the previous stop, or from the ETA anchor for the first remaining stop. Stamped
   * by the ETA engine alongside `eta`; absent when the last computation produced no geometry for
   * this leg.
   */
  routePolyline?: string;
  timeSlot?: TimeSlot;
  /** Canonical driver comment for this stop's address, stamped at insert time (see insertTaskStops). */
  userComment?: UserComment;
}

/**
 * A hydrated job: the raw `StopJob` reference resolved to its full `TaskJob`, `Task`, and
 * `Order`. Used by the read path of GET /queue.
 */
export interface EnrichedStopJob {
  order: Order;
  task: Task;
  taskJob: TaskJob;
}

/**
 * A hydrated stop: the persisted stop's `id` plus its surviving hydrated jobs. `fullAddress`
 * and `timeSlot` are copied from the persisted `Stop`.
 */
export interface EnrichedStop {
  /** Copied verbatim from the persisted `Stop.eta` (stamped by the ETA engine) on the read path. */
  eta?: Eta;
  fullAddress: string;
  id: string;
  jobs: EnrichedStopJob[];
  /** Decoded `routePolyline`, as `[lng, lat]` points (MapLibre order). Absent when no geometry is stored. */
  route?: [number, number][];
  timeSlot?: TimeSlot;
  userComment?: UserComment;
}

/**
 * The driver's persisted ongoing queue.
 * The `stops` array is ordered: array order IS the execution order.
 */
export interface StopsQueue extends BaseDocument {
  carrierId: string;
  driverId: string;
  /**
   * Schema-only — no writer yet. Will be stamped by the upcoming tour-planning stories once a
   * queue can be planned ahead of its execution day.
   */
  plannedEndDate?: Date;
  /** Schema-only — no writer yet. See `plannedEndDate`. */
  plannedStartDate?: Date;
  /**
   * When the queue flipped to `ended`. Time-source rule: for driver-declared terminal actions
   * (completing/failing the last job) this is the CLIENT-declared timestamp — an offline
   * completion made at 08:00 and replayed at 08:30 stamps 08:00; for dispatcher/server-driven
   * ends (order-cancel cascade, task release/reassign) this is the server time of the ending
   * event. Clamped to `[realStartDate ?? createdAt, server now]` before being stamped (billing
   * hardening, PHA-745 follow-up — `realEndDate` is a Forfait/Solo billing input): a queue cannot
   * end before it started (or before it existed, if never started) nor in the future.
   */
  realEndDate?: Date;
  /**
   * When the driver actually started executing the queue, stamped atomically with the first
   * successful `navigate` transaction (billing hardening, PHA-745 follow-up — `realStartDate` is a
   * Forfait/Solo billing input). Time-source rule: the client-declared tap time, clamped to
   * `[queue.createdAt, server now]`, when the navigate request carries one (offline replay
   * included); server now otherwise. Earliest-wins: a later or replayed navigate never moves it
   * forward; an out-of-order offline replay carrying an earlier (post-clamp) tap time moves it back.
   */
  realStartDate?: Date;
  /**
   * Set when the driver manually reorders the queue (`PATCH /queue/reorder`). When present,
   * newly inserted stops append at the end of the queue instead of timeslot-insertion. Absent
   * means the queue was never manually reordered.
   */
  reorderedAt?: Date;
  /** Last computed by the ETA engine (every recompute) or a confirmed optimization — absent until the first computation ever lands. */
  routeSummary?: StopsQueueRouteSummary;
  status: StopsQueueStatus;
  stops: Stop[];
  /** Stamped lazily on first touch after creation (see `VehicleProfileStamperService`). Absent means resolution failed or hasn't run yet — the routing request falls back to a lightweight-vehicle profile. */
  vehicleProfile?: RoutingVehicleProfile;
}

/** Response of `PATCH /queue/reorder`: the hydrated queue plus any pickup stop ids the server had to reposition. */
export interface ReorderQueueResponse {
  correctedStopIds: string[];
  stops: EnrichedStop[];
}

export const STOP_COMMENT_TARGET_KIND = {
  CUSTOMER_ADDRESS: 'customerAddress',
  SITE: 'site',
} as const;
export type StopCommentTargetKind = (typeof STOP_COMMENT_TARGET_KIND)[keyof typeof STOP_COMMENT_TARGET_KIND];

/** Where a stop's driver comment canonically lives, resolved from the order + job type. */
export type StopCommentTarget =
  | { customerId: string; kind: typeof STOP_COMMENT_TARGET_KIND.CUSTOMER_ADDRESS; originalAddress: string }
  | { kind: typeof STOP_COMMENT_TARGET_KIND.SITE; siteId: string };
